// TMOS System Logs Viewer Page
// Date: November 15, 2025
// Purpose: Platform admin page for viewing system logs

import React, { useState, useEffect } from 'react';
import { 
  DocumentTextIcon,
  FunnelIcon,
  XMarkIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { logViewerService, LogEntry, LogFilters } from '@/services/logViewerService';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';

export const SystemLogsPage: React.FC = () => {
  const { t } = useLanguage();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<LogFilters>({
    source: 'all',
    limit: 100
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadLogs();
  }, [filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const data = await logViewerService.getLogs(filters);
      setLogs(data);
    } catch (error: any) {
      console.error('Error loading logs:', error);
      toast.error(error.response?.data?.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  const getLevelBadgeColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error': return 'bg-red-100 text-red-800';
      case 'warn': return 'bg-yellow-100 text-yellow-800';
      case 'info': return 'bg-blue-100 text-blue-800';
      case 'debug': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const clearFilters = () => {
    setFilters({ source: 'all', limit: 100 });
  };

  return (
    <div className="min-h-screen bg-[var(--color-surface-bg)]">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DocumentTextIcon className="w-8 h-8 text-[var(--color-primary-bg)]" />
              <div>
                <h1 className="text-3xl font-bold text-[var(--color-surface-text)]">
                  {t('admin.logs.title') || 'System Logs'}
                </h1>
                <p className="text-[var(--color-secondary-text)]">
                  {t('admin.logs.subtitle') || 'View and search system logs'}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={loadLogs}
            >
              <ArrowPathIcon className="w-5 h-5 mr-2" />
              {t('common.refresh') || 'Refresh'}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card variant="outline" padding="md" className="mb-6">
          <CardContent>
            <div className="flex items-center gap-4 flex-wrap">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FunnelIcon className="w-5 h-5 mr-2" />
                {t('admin.logs.filters') || 'Filters'}
              </Button>
              {(filters.level || filters.source || filters.start_date || filters.end_date || filters.search) && (
                <Button
                  variant="outline"
                  onClick={clearFilters}
                >
                  <XMarkIcon className="w-5 h-5 mr-2" />
                  {t('admin.logs.clearFilters') || 'Clear'}
                </Button>
              )}
            </div>

            {showFilters && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('admin.logs.source') || 'Source'}
                  </label>
                  <select
                    value={filters.source || 'all'}
                    onChange={(e) => setFilters({ ...filters, source: e.target.value as 'database' | 'file' | 'all' })}
                    className="w-full px-3 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                  >
                    <option value="all">{t('admin.logs.allSources') || 'All Sources'}</option>
                    <option value="database">{t('admin.logs.database') || 'Database'}</option>
                    <option value="file">{t('admin.logs.file') || 'File'}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('admin.logs.level') || 'Level'}
                  </label>
                  <select
                    value={filters.level || ''}
                    onChange={(e) => setFilters({ ...filters, level: e.target.value as 'info' | 'warn' | 'error' | 'debug' | undefined })}
                    className="w-full px-3 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                  >
                    <option value="">{t('admin.logs.allLevels') || 'All Levels'}</option>
                    <option value="error">{t('admin.logs.error') || 'Error'}</option>
                    <option value="warn">{t('admin.logs.warn') || 'Warning'}</option>
                    <option value="info">{t('admin.logs.info') || 'Info'}</option>
                    <option value="debug">{t('admin.logs.debug') || 'Debug'}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('admin.logs.startDate') || 'Start Date'}
                  </label>
                  <input
                    type="datetime-local"
                    value={filters.start_date ? new Date(filters.start_date).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
                    className="w-full px-3 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('admin.logs.endDate') || 'End Date'}
                  </label>
                  <input
                    type="datetime-local"
                    value={filters.end_date ? new Date(filters.end_date).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
                    className="w-full px-3 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('admin.logs.search') || 'Search'}
                  </label>
                  <input
                    type="text"
                    placeholder={t('admin.logs.searchPlaceholder') || 'Search log messages...'}
                    value={filters.search || ''}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value || undefined })}
                    className="w-full px-3 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Logs List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-[var(--color-secondary-text)]">{t('common.loading') || 'Loading...'}</p>
          </div>
        ) : logs.length === 0 ? (
          <Card variant="outline" padding="lg">
            <CardContent className="text-center py-12">
              <DocumentTextIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-[var(--color-secondary-text)]">
                {t('admin.logs.noLogs') || 'No logs found'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card variant="outline" padding="none">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">{t('admin.logs.timestamp') || 'Timestamp'}</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">{t('admin.logs.level') || 'Level'}</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">{t('admin.logs.message') || 'Message'}</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">{t('admin.logs.source') || 'Source'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-border)]">
                    {logs.map((log, index) => (
                      <tr key={index} className="hover:bg-[var(--color-secondary-bg)]">
                        <td className="px-4 py-3 text-sm text-[var(--color-surface-text)]">
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getLevelBadgeColor(log.level)}`}>
                            {log.level.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-[var(--color-surface-text)]">
                          <div className="max-w-2xl truncate" title={log.message}>
                            {log.message}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-[var(--color-secondary-text)]">
                          {log.source}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};


